import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import api from "../../lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import CategoryModal from "./CategoryModal";
import DeleteConfirmDialog from "@/components/ui/DeleteConfirmDialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/Table";
import {
    Plus,
    Search,
    Building2,
    GripVertical,
    Copy,
    Check,
} from "lucide-react";
import EditIcon from "@/components/icons/EditIcon";
import DeleteIcon from "@/components/icons/DeleteIcon";
import CloseIcon from "@/components/icons/CloseIcon";
import toast from "react-hot-toast";

const CategoryEditPage = () => {
    const { id } = useParams(); // Mine type ID from URL
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [categoryModal, setCategoryModal] = useState({
        isOpen: false,
        category: null,
        mode: "create",
    });
    const [deleteDialog, setDeleteDialog] = useState({
        isOpen: false,
        category: null,
    });

    // Debounce search input
    useEffect(() => {
        if (search !== debouncedSearch) {
            setIsSearching(true);
        }

        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setIsSearching(false);
        }, 500);

        return () => clearTimeout(timer);
    }, [search, debouncedSearch]);

    // Fetch mine type data
    const { data: mineTypeData, isLoading: isLoadingMineType } = useQuery(
        ["mine-type", id],
        async () => {
            const response = await api.get(`/mine-type/${id}`);
            return response.data.data;
        },
        {
            enabled: !!id,
        }
    );

    // Redirect to view page if mine type is used in reports
    useEffect(() => {
        if (mineTypeData?.reports_count && mineTypeData.reports_count > 0) {
            navigate(`/mine-types/${id}/category/view`, { replace: true });
        }
    }, [mineTypeData, id, navigate]);

    // Fetch categories
    const { data: categoriesData, isLoading: isLoadingCategories, refetch: refetchCategories } = useQuery(
        ["mine-type-categories", id, debouncedSearch],
        async () => {
            const params = new URLSearchParams();
            if (debouncedSearch) {
                params.append("search", debouncedSearch);
            }
            const response = await api.get(`/mine-type/${id}/category?${params}`);
            return response.data.data || [];
        },
        {
            enabled: !!id,
            keepPreviousData: true,
        }
    );

    useEffect(() => {
        if (categoriesData) {
            setCategories(categoriesData);
            setIsLoading(false);
        } else if (isLoadingCategories) {
            setIsLoading(true);
        }
    }, [categoriesData, isLoadingCategories]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
    };

    const handleClearFilters = () => {
        setSearch("");
    };

    const handleCreateClick = () => {
        setCategoryModal({
            isOpen: true,
            category: null,
            mode: "create",
        });
    };

    const handleEditClick = (category) => {
        setCategoryModal({
            isOpen: true,
            category: category,
            mode: "edit",
        });
    };

    const handleDeleteClick = (category) => {
        setDeleteDialog({
            isOpen: true,
            category: category,
        });
    };

    const deleteMutation = useMutation(
        (categoryId) => api.delete(`/mine-type/${id}/category/${categoryId}`),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["mine-type-categories", id]);
                toast.success("Category deleted successfully");
                setDeleteDialog({ isOpen: false, category: null });
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || "Failed to delete category");
            },
        }
    );

    const handleDeleteConfirm = () => {
        if (deleteDialog.category) {
            deleteMutation.mutate(deleteDialog.category.id);
        }
    };

    // Drag and Drop handlers
    const handleDragStart = (e, index) => {
        // Don't start drag if clicking on buttons or icons
        if (e.target.closest('button') || e.target.closest('svg')) {
            e.preventDefault();
            return;
        }
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/html", e.target.outerHTML);
        e.target.style.opacity = "0.5";
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = "1";
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (draggedIndex !== null && draggedIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const updateOrderMutation = useMutation(
        (categoryIds) => api.put(`/mine-type/${id}/category/order`, { categoryIds }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["mine-type-categories", id]);
                toast.success("Category order updated");
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || "Failed to update category order");
                // Revert to original order on error
                refetchCategories();
            },
        }
    );

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDragOverIndex(null);
            return;
        }

        const newCategories = [...categories];
        const draggedItem = newCategories[draggedIndex];

        // Remove the dragged item
        newCategories.splice(draggedIndex, 1);

        // Insert at new position
        newCategories.splice(dropIndex, 0, draggedItem);

        setCategories(newCategories);
        setDraggedIndex(null);
        setDragOverIndex(null);

        // Update order via API
        const categoryIds = newCategories.map((cat) => cat.id);
        updateOrderMutation.mutate(categoryIds);
    };

    const generateRiskKey = (riskName) => {
        if (!riskName) return "";
        const key = riskName
            .replace(/[^a-zA-Z0-9\s]/g, ' ')
            .trim()
            .replace(/\s+/g, '_')
            .toUpperCase();
        return `{{IMAGE ${key}_SECTION}}`;
    };

    const generateCategoryKey = (categoryName) => {
        if (!categoryName) return "";
        const key = categoryName
            .replace(/[^a-zA-Z0-9\s]/g, ' ')
            .trim()
            .replace(/\s+/g, '_')
            .toUpperCase();
        return `{{IMAGE RISK_SCORE_${key}_TABLE}}`;
    };

    return (
        <div className="min-h-screen">
            <div className="">
                {/* Header */}
                {mineTypeData && (
                    <div className="mb-4 slide-up">
                        <p className="text-lg font-semibold text-gray-0">Mine Type: {mineTypeData.name}</p>
                    </div>
                )}
                <div className="flex flex-col gap-3 slide-up lg:flex-row lg:items-center lg:justify-between">
                    <div className="slide-up">
                        <Button
                            onClick={handleCreateClick}
                            className="w-full text-sm hover:bg-red-900 sm:w-auto sm:text-base"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Section
                        </Button>
                    </div>
                    {/* Filters */}
                    <div className="flex flex-col w-full gap-3 lg:w-auto">
                        <div className="flex flex-col w-full gap-3 sm:flex-row sm:items-end lg:justify-end">
                            <div className="flex-1 min-w-0 sm:max-w-xs">
                                <div className="relative">
                                    <Input
                                        placeholder="Search sections..."
                                        value={search}
                                        onChange={handleSearchChange}
                                        className="w-full pr-10"
                                    />
                                    <Search className="absolute w-4 h-4 ml-4 transform -translate-y-1/2 right-3 top-1/2 text-gray-0" />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    onClick={handleClearFilters}
                                    className="w-full text-sm hover:bg-red-900 sm:w-auto sm:text-base"
                                >
                                    <CloseIcon className="w-5 h-5 mr-2" />
                                    Clear
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 mt-2 gap-2 sm:grid-cols-2 slide-up">
                    <div className="flex min-w-0 items-center justify-between gap-2 rounded-lg bg-navy-100 p-4">
                        <div className="min-w-0 truncate text-gray-0">Risk Quality Chart</div>
                        <CopyButton text="{{IMAGE RISK_QUALITY_CHART}}" />
                    </div>
                    <div className="flex min-w-0 items-center justify-between gap-2 rounded-lg bg-navy-100 p-4">
                        <div className="min-w-0 truncate text-gray-0">Loss Bar Chart</div>
                        <CopyButton text="{{IMAGE LOSS_BAR_CHART}}" />
                    </div>
                    <div className="flex min-w-0 items-center justify-between gap-2 rounded-lg bg-navy-100 p-4">
                        <div className="min-w-0 truncate text-gray-0">Business Interruption Chart</div>
                        <CopyButton text="{{IMAGE BUSINESS_INTERRUPTION_CHART}}" />
                    </div>
                    <div className="flex min-w-0 items-center justify-between gap-2 rounded-lg bg-navy-100 p-4">
                        <div className="min-w-0 truncate text-gray-0">Recommendation Chart</div>
                        <CopyButton text="{{IMAGE RECOMMENDATION_CHART}}" />
                    </div>
                </div>

                {/* Table */}
                {isLoading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <div className="w-8 h-8 mx-auto mb-4 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
                            <p className="text-gray-600">Loading sections...</p>
                        </div>
                    </div>
                ) : (
                    <div className="shadow-sm slide-up backdrop-blur-sm">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-gray-200 hover:bg-gray-50/50">
                                    <TableHead className="w-10"></TableHead>
                                    <TableHead className="sticky md:sticky md:left-0 md:top-0">Risk Section</TableHead>
                                    <TableHead>Risk Sub-Section</TableHead>
                                    <TableHead className="text-center">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isSearching ? (
                                    <TableRow>
                                        <TableCell colSpan={4} className="py-12 text-center">
                                            <div className="text-center">
                                                <LoadingSpinner size="lg" />
                                                <p className="mt-4 text-gray-600">Searching sections...</p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : !categories || categories.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} className="py-12 text-center">
                                            <div className="text-gray-500">
                                                <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                                <p>No sections found</p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    categories.map((category, index) => (
                                        <TableRow
                                            key={category.id}
                                            className={`whitespace-nowrap transition-colors ${dragOverIndex === index ? "bg-red-600/20" : ""
                                                }`}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragEnd={handleDragEnd}
                                            onDragOver={(e) => handleDragOver(e, index)}
                                            onDragLeave={handleDragLeave}
                                            onDrop={(e) => handleDrop(e, index)}
                                        >
                                            <TableCell className="w-10">
                                                <div className="flex items-center justify-center p-2">
                                                    <GripVertical className="w-5 h-5 text-gray-400" />
                                                </div>
                                            </TableCell>
                                            <TableCell className="sticky md:sticky md:left-0 md:top-0">
                                                <div className="p-4 my-2 mr-2 rounded-t-lg bg-navy-100 flex items-center justify-between gap-2">
                                                    <div className="text-gray-0">{category.risk_name}</div>
                                                    <CopyButton text={generateRiskKey(category.risk_name)} />
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="p-4 m-2 rounded-t-lg bg-navy-100 text-gray-0 flex items-center justify-between gap-2">
                                                    {category.category_name}
                                                    <CopyButton text={generateCategoryKey(category.category_name)} />
                                                </div>
                                            </TableCell>
                                            <TableCell className="w-4 text-right">
                                                <div className="p-2 my-2 ml-2 text-center rounded-t-lg bg-navy-100 text-gray-0">
                                                    <div
                                                        className="flex items-center justify-center gap-2"
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <Button
                                                            variant="outline p-0"
                                                            size="sm"
                                                            className="rounded-lg"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEditClick(category);
                                                            }}
                                                        >
                                                            <EditIcon className="w-5 h-5 text-white transition-transform duration-200 hover:text-red-500 hover:scale-110" />
                                                        </Button>
                                                        <Button
                                                            variant="outline p-0"
                                                            size="sm"
                                                            className="rounded-lg"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteClick(category);
                                                            }}
                                                        >
                                                            <DeleteIcon className="w-5 h-5 text-white transition-transform duration-200 hover:text-red-500 hover:scale-110" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
                <div className="flex flex-col pr-0 border-t slide-up border-gray-0 sm:flex-row sm:items-center sm:justify-between sm:pr-12">
                </div>

                {/* Category Modal */}
                <CategoryModal
                    isOpen={categoryModal.isOpen}
                    onClose={() => setCategoryModal({ isOpen: false, category: null, mode: "create" })}
                    category={categoryModal.category}
                    mode={categoryModal.mode}
                    mineTypeId={id}
                    onSuccess={() => {
                        queryClient.invalidateQueries(["mine-type-categories", id]);
                    }}
                />

                {/* Delete Confirmation Dialog */}
                <DeleteConfirmDialog
                    isOpen={deleteDialog.isOpen}
                    onClose={() => setDeleteDialog({ isOpen: false, category: null })}
                    onConfirm={handleDeleteConfirm}
                    title="Delete Category"
                    userName={deleteDialog.category ? `${deleteDialog.category.risk_name} - ${deleteDialog.category.category_name}` : ""}
                    isLoading={deleteMutation.isLoading}
                />
            </div>
        </div >
    );
};

const CopyButton = ({ text }) => {
    const [isCopied, setIsCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(text);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    return (
        <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 hover:bg-transparent"
            onClick={handleCopy}
            title="Copy Key"
        >
            {isCopied ? (
                <Check className="h-4 w-4 text-green-500" />
            ) : (
                <Copy className="h-4 w-4 text-gray-400 hover:text-red-500" />
            )}
        </Button>
    );
};

export default CategoryEditPage;

